import {
  type InfiniteData,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { ApiResponse } from '@/api/api-types'
import { bundlesService } from '@/api/bundles/bundles-service'
import type { Bundle } from '@/api/bundles/bundles-types'
import { ordersService } from '@/api/orders/orders-service'
import type { Order } from '@/api/orders/orders-types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { useBundleScanId, useSkidPanelKey } from '@/hooks/use-query-states'
import { cn } from '@/lib/utils'
import { useScanWarningStore } from '@/store/scan-warning'
import { useSkidPanelHasHydrated, useSkidPanelStore } from '@/store/skid-panel'
import { shouldWarnInterruptedScan } from './detect-interrupted-scan'

import { isBundleLockedForScan } from '@/utils/order-bundle-packaging'
import { useSkidLengthBump } from '@/hooks/use-skid-length-bump'
import {
  collectSkidsFromOrders,
  formatShippingUnitName
} from '@/utils/order-skids'
import {
  extractSkidLabelFromInput,
  formatSkidDisplayName,
  resolveSkidInputToId
} from '@/utils/skid-input-resolve'

function findBundleInOrders(orders: Order[], bundleId: number): { order: Order; bundle: Bundle } | null {
  for (const order of orders) {
    for (const detail of order.details ?? []) {
      for (const bundle of detail.bundles ?? []) {
        if (bundle.id === bundleId) return { order, bundle }
      }
    }
  }
  return null
}

export const BundleScanSheet = () => {
  const [bundleId, setBundleId] = useBundleScanId()
  const [skidKey, setSkidKey] = useSkidPanelKey()
  const queryClient = useQueryClient()
  // When a skid sheet is in bulk-scan-bundles mode, the skid sheet owns `?bundle=<id>`
  // processing — assigns the bundle and re-asserts `?skid=` so the next scan keeps
  // working. Defer entirely so we don't double-process or steal the user's flow.
  const bulkScan = useSkidPanelStore((s) => s.bulkScan)
  // Inverse of `bulkScan`: keeps the bundle sheet alive across an AHK navigation that
  // strips `?bundle=` and adds `?skid=`. Lets us consume the scanned skid as the
  // shipping-unit assignment instead of letting the skid sheet take over.
  const bundleScanActive = useSkidPanelStore((s) => s.bundleScanActive)
  const setBundleScanActive = useSkidPanelStore((s) => s.setBundleScanActive)
  // `persist` hydrates async — gate `open` on it so a transient `bulkScan == null`
  // before sessionStorage is read back doesn't pop this sheet over an active
  // bulk-scan-bundles session.
  const skidPanelHydrated = useSkidPanelHasHydrated()
  const flashWarning = useScanWarningStore((s) => s.flash)
  const bumpSkidLength = useSkidLengthBump()

  const effectiveBundleId = bundleId ?? bundleScanActive?.bundleId ?? null

  // When the order page already shows an inline Ship. Unit input for this bundle, defer to it
  // (focus the row input) instead of opening the side sheet.
  //
  // Why a layout effect instead of useMemo: the inline cell can mount AFTER this render
  // commits — e.g. an `?orders=<autoid>` URL param expands the order accordion, whose
  // AccordionContent (and thus the cell) only enters the DOM once the parent commits.
  // A useMemo querySelector during render would miss it and the side sheet would pop
  // on top of the inline input. The layout effect re-checks after the DOM commit and
  // again on the next frame, which catches Radix's animated content reveal.
  const [hasInlineRow, setHasInlineRow] = useState(false)
  useLayoutEffect(() => {
    if (effectiveBundleId == null || typeof document === 'undefined') {
      setHasInlineRow(false)
      return
    }
    const selector = `input[data-packaging-scan-target="shipping-unit"][data-bundle-id="${effectiveBundleId}"]`
    const check = () => setHasInlineRow(Boolean(document.querySelector(selector)))
    check()
    const r = requestAnimationFrame(check)
    return () => cancelAnimationFrame(r)
  }, [effectiveBundleId])

  const open = effectiveBundleId != null && !hasInlineRow && bulkScan == null && skidPanelHydrated

  // Targeted query: fetch the one order that owns this bundle via `?bundle_id=`.
  // A previous version fetched a capped `limit: 500` order list and searched it
  // client-side, which silently reported "Bundle not found" for any bundle whose
  // order fell outside the newest-500 window (real order count > 500, and the list
  // is not ordered by recency). Runs whenever there is an active scan target —
  // including the inline-row case where the side sheet stays closed but the AHK
  // URL-flip (`?skid=<label>`) still needs to be consumed and assigned to this
  // bundle. Keyed on `effectiveBundleId` so the cached data survives an AHK URL
  // flip that momentarily clears `?bundle=`.
  const { data: ordersRes, isLoading: ordersLoading } = useQuery({
    queryKey: ['bundle-scan-orders', effectiveBundleId],
    queryFn: () => ordersService.get({ bundle_id: effectiveBundleId! }),
    enabled: effectiveBundleId != null
  })

  // SKI/LHB are embedded on `order.skids[]` in the orders we just loaded above — derive from there
  // instead of issuing a separate `GET /skids/` request. Bundles can only target SKI/LHB (MOS/OUT
  // are rejected by resolveSkidInputToId), so this subset is sufficient.
  const skids = useMemo(
    () => collectSkidsFromOrders(ordersRes?.results ?? []),
    [ordersRes?.results]
  )

  const found = useMemo(
    () =>
      effectiveBundleId != null
        ? findBundleInOrders(ordersRes?.results ?? [], effectiveBundleId)
        : null,
    [ordersRes?.results, effectiveBundleId]
  )

  const currentSkid =
    found?.bundle.skid_id != null ? skids.find((s) => s.id === found.bundle.skid_id) ?? null : null

  const [value, setValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) setValue('')
  }, [open, effectiveBundleId])

  // Mark a bundle-scan session active in the store as soon as `?bundle=<id>` lands in
  // the URL — even when the side sheet is hidden (`hasInlineRow=true` for the order's
  // expanded detail row). The flag pins SkidDetailsSheet closed (it gates on
  // `bundleScanActive == null`) so the next AHK URL flip from `?bundle=<id>` to
  // `?skid=<label>` doesn't pop the skid sheet over the user.
  //
  // Skipped when SkidDetailsSheet is in a bulk-scan session (`bulkScan != null`):
  // SKI/LHB bulk-scan-bundles owns `?bundle=<id>` processing and re-pins `?skid=`
  // afterwards — pinning the bundle sheet active there would close the skid sheet
  // for the rest of the session.
  useEffect(() => {
    if (bundleId == null) return
    if (bulkScan != null) return
    if (bundleScanActive?.bundleId === bundleId) return

    // Operator complaint: "if he missed scanning a skid ID label and then scanned
    // the next bundle it really seemed to mess things up." Detect that here —
    // the bundleId is changing from a previous bundle that never had a commit
    // attempt and is still unassigned in the orders cache. Toast a non-blocking
    // warning so the operator can go back and scan the missed skid before the
    // pallet leaves.
    const prevBundleId = bundleScanActive?.bundleId
    if (prevBundleId != null) {
      // Read the previous bundle's order from its own cached `bundle-scan-orders`
      // entry, not the current `ordersRes` — the targeted `?bundle_id=` fetch only
      // returns the NEW bundle's order, so it never contains prev. That cache entry
      // was populated while prev was active and is patched optimistically by both
      // the sheet and inline-cell assign mutations, so it reflects a skid assigned
      // via either path.
      const prevData = queryClient.getQueryData<ApiResponse<Order>>([
        'bundle-scan-orders',
        prevBundleId
      ])
      const prev = findBundleInOrders(prevData?.results ?? [], prevBundleId)
      if (
        shouldWarnInterruptedScan({
          prevBundleId,
          newBundleId: bundleId,
          committedForPrev: committedForBundleRef.current,
          prevBundleSkidId: prev?.bundle.skid_id
        })
      ) {
        toast.warning(
          `Bundle #${prevBundleId} still needs a shipping unit — scan a skid for it before moving on.`,
          { id: `interrupted-${prevBundleId}`, duration: 6000 }
        )
      }
    }

    setBundleScanActive({ bundleId })
  }, [bundleId, bulkScan, bundleScanActive?.bundleId, setBundleScanActive, queryClient])

  // Clear `bundleScanActive` shortly after both URL params are gone — same pattern as the
  // skid bulk-scan grace period, but with a longer window. Older AHK helpers still navigate
  // to `?skid=<label>` ~250ms after the React debounce already processed the input; the
  // longer grace lets us swallow that late navigation (intercepted as a duplicate scan and
  // discarded) instead of opening the skid sheet on top.
  useEffect(() => {
    if (bundleScanActive == null) return
    if (bundleId != null || skidKey != null) return
    const t = window.setTimeout(() => setBundleScanActive(null), 800)
    return () => window.clearTimeout(t)
  }, [bundleId, skidKey, bundleScanActive, setBundleScanActive])

  useEffect(() => {
    if (!open || !found || currentSkid) return
    const focusInput = () => {
      const el =
        inputRef.current ??
        (document.querySelector(
          'input[data-packaging-scan-target="shipping-unit"]'
        ) as HTMLInputElement | null)
      if (el) {
        el.focus()
        el.select?.()
      }
    }
    // Retry across Radix focus-trap passes and sheet animation.
    const ids = [50, 150, 350, 600].map((ms) => window.setTimeout(focusInput, ms))
    return () => ids.forEach((id) => window.clearTimeout(id))
  }, [open, found?.bundle.id, currentSkid])

  // Hard close — used by manual user actions (close button, sheet overlay click).
  // Drops `bundleScanActive` immediately so a follow-up skid scan opens the skid sheet
  // normally instead of being swallowed by the AHK-race grace window.
  const closeBundleScan = () => {
    setBundleId(null)
    setSkidKey(null)
    setBundleScanActive(null)
  }

  // Soft close — used after a successful mutation. Leaves `bundleScanActive` set so the
  // grace-period effect clears it ~800ms later. That window catches the older AHK
  // helper's late navigation to `?skid=<label>` and prevents the skid sheet from
  // popping over the user's view.
  const closeAfterMutation = () => {
    setBundleId(null)
    setSkidKey(null)
  }

  const assignMutation = useMutation({
    mutationFn: (skid_id: number | null) =>
      bundlesService.multiUpdate({ ids: [effectiveBundleId!], skid_id, is_complete: true }),
    meta: {
      successMessage: 'Bundle assigned and marked complete',
      errorMessage: 'Failed to assign bundle'
    },
    // Optimistically patch every cached order shape so the bundle row reflects
    // the new skid_id + is_complete the moment the operator's scan commits, and
    // the sheet closes on the same frame. The background invalidation in
    // onSuccess reconciles afterwards. Operator complaint was a visible lag
    // between scan and sheet-close that triggered re-scans.
    onMutate: async (skid_id) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['orders'] }),
        queryClient.cancelQueries({ queryKey: ['order'] }),
        queryClient.cancelQueries({ queryKey: ['bundle-scan-orders'] })
      ])
      const ordersSnap = queryClient.getQueriesData({ queryKey: ['orders'] })
      const orderSnap = queryClient.getQueriesData({ queryKey: ['order'] })
      const scanSnap = queryClient.getQueriesData({ queryKey: ['bundle-scan-orders'] })

      const patchOrder = (o: Order): Order => ({
        ...o,
        details: (o.details ?? []).map((d) => ({
          ...d,
          bundles: (d.bundles ?? []).map((b) =>
            b.id === effectiveBundleId
              ? { ...b, skid_id, is_complete: true }
              : b
          )
        }))
      })

      // ['orders', ...] is useInfiniteQuery (pages[]).
      queryClient.setQueriesData<InfiniteData<ApiResponse<Order>> | undefined>(
        { queryKey: ['orders'] },
        (old) =>
          old?.pages
            ? {
                ...old,
                pages: old.pages.map((p) => ({
                  ...p,
                  results: p.results.map(patchOrder)
                }))
              }
            : old
      )
      // ['order', id] is a single Order.
      queryClient.setQueriesData<Order | undefined>(
        { queryKey: ['order'] },
        (old) => (old && 'details' in old ? patchOrder(old) : old)
      )
      // ['bundle-scan-orders', bundleId] is { results: Order[] }.
      queryClient.setQueriesData<ApiResponse<Order> | undefined>(
        { queryKey: ['bundle-scan-orders'] },
        (old) =>
          old?.results ? { ...old, results: old.results.map(patchOrder) } : old
      )

      closeAfterMutation()
      return { ordersSnap, orderSnap, scanSnap }
    },
    onError: (_err, _vars, ctx) => {
      // Failed assign must stay retryable — clear the per-scan dedupe set so a
      // re-scan of the same skid isn't swallowed as a duplicate. The two-path
      // race this set guards has already resolved by the time the mutation errors.
      processedSkidLabelsRef.current.clear()
      if (!ctx) return
      for (const [key, data] of ctx.ordersSnap) queryClient.setQueryData(key, data)
      for (const [key, data] of ctx.orderSnap) queryClient.setQueryData(key, data)
      for (const [key, data] of ctx.scanSnap) queryClient.setQueryData(key, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
      queryClient.invalidateQueries({ queryKey: ['skids'] })
      queryClient.invalidateQueries({ queryKey: ['bundle-scan-orders'] })
      // Grow the skid when the scanned part sticks out past its current length.
      const pending = pendingLengthBumpRef.current
      pendingLengthBumpRef.current = null
      if (pending) {
        const target = skids.find((s) => s.id === pending.skidId)
        bumpSkidLength(pending.skidId, target?.length, pending.detailLength)
      }
      // closeAfterMutation already fired in onMutate.
    }
  })

  const unassignMutation = useMutation({
    mutationFn: () => bundlesService.multiUpdate({ ids: [effectiveBundleId!], skid_id: null }),
    meta: {
      successMessage: 'Bundle unassigned',
      errorMessage: 'Failed to unassign bundle'
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
      queryClient.invalidateQueries({ queryKey: ['skids'] })
      queryClient.invalidateQueries({ queryKey: ['bundle-scan-orders'] })
    }
  })

  const completeMutation = useMutation({
    mutationFn: () => bundlesService.multiUpdate({ ids: [effectiveBundleId!], is_complete: true }),
    meta: {
      successMessage: 'Bundle marked complete',
      errorMessage: 'Failed to complete bundle'
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
      queryClient.invalidateQueries({ queryKey: ['skids'] })
      queryClient.invalidateQueries({ queryKey: ['bundle-scan-orders'] })
      closeAfterMutation()
    }
  })

  // If the bundle already belongs to a skid (i.e. it was scanned once before), mark it complete
  // automatically on re-scan. Skip when it is already complete. Otherwise the scan input stays
  // focused waiting for the shipping-unit scan.
  const bundleOnSkid = useMemo(() => {
    if (effectiveBundleId == null) return null
    for (const s of skids) {
      const b = s.bundles?.find((x) => x.id === effectiveBundleId)
      if (b) return { skid: s, bundle: b }
    }
    return null
  }, [skids, effectiveBundleId])

  const fallbackIsComplete = found?.bundle.is_complete === true
  const autoCompleteFiredRef = useRef<number | null>(null)
  useEffect(() => {
    if (!open) {
      autoCompleteFiredRef.current = null
      return
    }
    // Already complete → nothing to do; close sheet with info. Soft-close so a tail-end
    // AHK navigation that lands on `?skid=<label>` is also swallowed by the grace window.
    if (bundleOnSkid && (bundleOnSkid.bundle.is_complete === true || fallbackIsComplete)) {
      toast.info('Bundle is already complete.', { id: `bundle-complete-${bundleOnSkid.bundle.id}` })
      closeAfterMutation()
      return
    }
    if (!bundleOnSkid) return
    if (autoCompleteFiredRef.current === bundleOnSkid.bundle.id) return
    if (completeMutation.isPending) return
    autoCompleteFiredRef.current = bundleOnSkid.bundle.id
    completeMutation.mutate()
  }, [open, bundleOnSkid, fallbackIsComplete])

  // Per-session dedupe set keyed by lowercased skid label. Both the input-debounce path
  // (URL string typed into the field) and the URL-flip path (older AHK navigates the
  // browser to `?skid=<label>`) feed through `commit()`; without this, a single physical
  // scan that fires both paths would issue two mutations.
  const processedSkidLabelsRef = useRef<Set<string>>(new Set())
  /** Skid + scanned part length handed from `commit()` to the mutation's `onSuccess`. */
  const pendingLengthBumpRef = useRef<{ skidId: number; detailLength: number | null } | null>(null)

  // Stamped at the start of every commit() — even before short-circuits — so the
  // interrupted-session detector below can tell "operator did try to scan a skid for
  // this bundle" from "operator skipped the skid step entirely". Cleared implicitly by
  // overwrite when the next commit lands; never need to reset to null because the
  // detector compares against the *previous* bundle id, not against null.
  const committedForBundleRef = useRef<number | null>(null)

  useEffect(() => {
    processedSkidLabelsRef.current = new Set()
  }, [effectiveBundleId])

  const commit = (raw: string) => {
    if (effectiveBundleId == null || !found) return
    // Stamp first so the interrupted-session detector sees the attempt even if we
    // short-circuit on already-complete or wrong-skid below.
    committedForBundleRef.current = effectiveBundleId
    // Skid QR codes encode a packaging URL (`...?skid=<label>`); pull the label out
    // before resolving. Plain-label and numeric-id inputs pass through unchanged.
    const skidInput = extractSkidLabelFromInput(raw)
    const dedupeKey = skidInput.trim().toLowerCase()
    // Swallow the second path of a single physical scan within THIS sheet: the
    // input-debounce path and the AHK URL-flip path both feed commit(); the first stamps
    // the label here so the second is a silent no-op.
    if (dedupeKey && processedSkidLabelsRef.current.has(dedupeKey)) {
      closeAfterMutation()
      return
    }
    const resolved = resolveSkidInputToId(skidInput, skids, {
      orderAutoid: found.order.autoid,
      expect: 'bundle-target'
    })
    if (!resolved.ok) {
      toast.error(resolved.message, { id: `scan-error-${resolved.code}-${raw}` })
      flashWarning(resolved.message, { title: 'Wrong skid' })
      // Drop the bad label from the URL so it doesn't survive into the next
      // render or re-trigger commit() via the AHK-flip effect below.
      setSkidKey(null)
      setValue('')
      return
    }
    if (resolved.skid_id === null) {
      setValue('')
      return
    }
    // Scanned skid is already the one on this bundle → silent no-op, resolved BEFORE the
    // lock guard. In the inline flow the two paths of one physical scan land in two
    // components (this sheet's AHK URL-flip + the inline BundleSkidIdCell's input
    // debounce) whose dedupe sets are independent — so `processedSkidLabelsRef` above
    // can't catch the second path. It arrives here after the cell already assigned the
    // skid and flipped is_complete=true; a matching skid_id means duplicate, not reassign,
    // so swallow it instead of tripping the "already complete" block. A DIFFERENT skid on
    // a complete bundle still blocks below.
    if (resolved.skid_id === found.bundle.skid_id) {
      if (dedupeKey) processedSkidLabelsRef.current.add(dedupeKey)
      closeAfterMutation()
      return
    }
    if (isBundleLockedForScan(found.bundle)) {
      // Pair the toast with the full-screen overlay (same pattern as the WRONG SKID
      // branch above). The bottom-right toast was easy to miss in a busy packaging
      // session — the operator scanned, saw nothing happen, and started troubleshooting
      // the scanner instead of unchecking "Complete" on the bundle.
      const message =
        'Bundle is already complete. Uncheck "Complete" first to change the shipping unit.'
      toast.error(message, { id: `scan-complete-${effectiveBundleId}` })
      flashWarning(message, { title: 'Already complete' })
      closeAfterMutation()
      return
    }
    if (dedupeKey) processedSkidLabelsRef.current.add(dedupeKey)
    // Capture what the skid has to grow to before `onMutate` clears the URL params that
    // `found` derives from; `onSuccess` can no longer see the bundle by then.
    const scannedDetail = found.order.details?.find((d) =>
      d.bundles?.some((b) => b.id === found.bundle.id)
    )
    pendingLengthBumpRef.current = {
      skidId: resolved.skid_id,
      detailLength: scannedDetail?.heightd ?? null
    }
    assignMutation.mutate(resolved.skid_id)
  }

  // Auto-fire after scanner finishes typing (debounce).
  useEffect(() => {
    if (!open || !found || !value.trim() || assignMutation.isPending) return
    const t = window.setTimeout(() => commit(value), 100)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, open, found?.bundle.id])

  // AHK-flip path: AHK Ctrl+L navigation flips the URL from `?bundle=<id>` to
  // `?skid=<label>`. Consume the label as the shipping-unit assignment instead of
  // letting the skid sheet open. NOT gated on `open` — when the packaging list
  // shows an inline Ship. Unit row for this bundle the side sheet is intentionally
  // suppressed (`hasInlineRow=true`), but the scan still needs to be assigned.
  // Without this branch the URL change would be silently dropped after the grace
  // window cleared `bundleScanActive`. `processedSkidLabelsRef` dedupes against
  // the input-debounce path that may have already fired for this scan.
  useEffect(() => {
    if (effectiveBundleId == null || !found) return
    if (skidKey == null) return
    if (assignMutation.isPending) return
    commit(skidKey)
    setSkidKey(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skidKey, effectiveBundleId, found?.bundle.id])

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) closeBundleScan()
      }}
    >
      <SheetContent
        side='right'
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          requestAnimationFrame(() => inputRef.current?.focus())
        }}
        className={cn(
          'flex w-full max-w-md flex-col gap-0 overflow-hidden p-0 sm:max-w-md',
          '[&_.sheet-close]:top-3.5'
        )}
      >
        {effectiveBundleId == null ? null : ordersLoading ? (
          <div className='flex flex-1 flex-col gap-4 p-6'>
            <Skeleton className='h-8 w-48' />
            <Skeleton className='h-4 w-64' />
            <Skeleton className='h-24 w-full' />
          </div>
        ) : !found ? (
          <div className='p-6'>
            <SheetHeader className='p-0'>
              <SheetTitle className='text-lg'>Bundle not found</SheetTitle>
            </SheetHeader>
            <p className='text-grey-600 mt-4 text-sm'>
              Bundle #{effectiveBundleId} could not be located. Check the sticker and try again.
            </p>
            <Button
              type='button'
              variant='outline'
              className='mt-6 w-full'
              onClick={closeBundleScan}
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <SheetHeader className='border-grey-200 space-y-3 border-b px-4 pt-5 pb-3'>
              <div className='min-w-0 pr-8'>
                <SheetTitle className='text-black-200 text-2xl font-bold tracking-tight'>
                  {found.bundle.id}
                </SheetTitle>
                <p className='text-grey-600 mt-1 text-sm'>
                  Order #{found.order.invoice} · {found.order.customer}
                </p>
              </div>
              <div className='bg-grey-100 text-grey-700 flex flex-wrap gap-x-6 gap-y-1 rounded-md px-3 py-2 text-xs'>
                <span>
                  <span className='text-grey-500'>Qty: </span>
                  {found.bundle.quantity}
                </span>
                <span>
                  <span className='text-grey-500'>Name: </span>
                  {found.bundle.name || '—'}
                </span>
              </div>
            </SheetHeader>

            <div className='min-h-0 flex-1 overflow-y-auto px-4 py-4'>
              {currentSkid ? (
                <div className='mb-4 rounded-md border border-blue-200 bg-blue-50/60 p-3 text-sm'>
                  <div className='text-blue-300 font-semibold'>
                    Already assigned to {formatShippingUnitName(currentSkid)}
                  </div>
                  <p className='text-grey-700 mt-1 text-xs'>
                    Scan a different shipping unit to move it, or unassign below.
                  </p>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    className='border-red-200 text-red-300 mt-2 h-7 px-2 text-xs'
                    disabled={unassignMutation.isPending || isBundleLockedForScan(found.bundle)}
                    title={
                      isBundleLockedForScan(found.bundle)
                        ? 'Uncheck "Complete" first to unassign.'
                        : undefined
                    }
                    onClick={() => {
                      if (isBundleLockedForScan(found.bundle)) {
                        toast.error(
                          'Bundle is already complete. Uncheck "Complete" first to unassign.',
                          { id: `scan-complete-${effectiveBundleId}` }
                        )
                        return
                      }
                      unassignMutation.mutate()
                    }}
                  >
                    Unassign
                  </Button>
                </div>
              ) : null}

              <label className='text-grey-700 mb-1 block text-xs font-medium'>
                Shipping unit
              </label>
              <Input
                ref={(el) => {
                  inputRef.current = el
                  if (el && !currentSkid) {
                    // Focus as soon as the input mounts while the sheet is open and the bundle
                    // still needs a shipping-unit scan. Runs on every remount (sheet re-opens).
                    requestAnimationFrame(() => el.focus())
                  }
                }}
                autoFocus
                type='text'
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder='Scan shipping unit QR'
                data-packaging-scan-target='shipping-unit'
                disabled={assignMutation.isPending}
                className='h-10 font-mono'
                aria-label='Shipping unit'
              />
              <p className='text-grey-500 mt-1 text-xs'>
                Current:{' '}
                <span className='text-grey-700 font-mono'>
                  {formatSkidDisplayName(found.bundle.skid_id, skids) || '—'}
                </span>
              </p>
            </div>

            <SheetFooter className='border-grey-200 bg-grey-100/80 mt-0 flex-col gap-2 border-t px-4 py-3'>
              <Button
                type='button'
                variant='outline'
                className='bg-grey-200 border-grey-300 w-full'
                onClick={closeBundleScan}
              >
                Close
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
